import { scale } from '@utils/normalize';

// Design Tokens - Core values that define the design system
export const tokens = {
  // Colors (migrated from colors.ts)
  colors: {
    transparent: 'transparent',
    black: 'black',
    primary: {
      carnation: '#F56B57',
      darkCarnation: '#A14335',
      softCarnation: '#FEF0EE',
    },
    secondary: {
      softOrchid: '#EBEFFE',
      orchid: '#3863F0',
      darkOrchid: '#223B90',
    },
    success: {
      softCactus: '#E6EFE3',
      cactus: '#56893E',
      darkCactus: '#2E6930',
    },
    warning: {
      softSunflower: '#FEF0DC',
      sunflower: '#F8B24F',
      darkSunflower: '#A17230',
    },
    error: {
      softRose: '#FEEAE8',
      rose: '#F23015',
      darkRose: '#911D0D',
    },
    neutral: {
      cotton: '#FFFFFF',
      mist: '#F4F4F4',
      darkMist: '#909090',
      charcoal: '#454545',
    },
    variants: {
      softClover: '#D5F2EA',
      clover: '#2CBE96',
      darkClover: '#1A725A',
    },
    disabled: {
      smoke: '#C9C9C9',
    },
  },

  // Typography
  typography: {
    fontFamilies: {
      regular: 'Nunito-Regular',
      bold: 'Nunito-Bold',
    },
    fontSizes: {
      xxxl: scale(32),
      xxl: scale(24),
      xl: scale(20),
      lg: scale(16),
      md: scale(14),
      sm: scale(12),
      xsm: scale(10),
    },
    lineHeights: {
      xxxl: scale(48),
      xxl: scale(36),
      xl: scale(30),
      lg: scale(24),
      md: scale(21),
      sm: scale(18),
      xsm: scale(15),
    },
    fontWeights: {
      normal: '400',
      bold: '700',
    },
  },

  // Spacing
  spacing: {
    xs: scale(4),
    sm: scale(8),
    md: scale(16),
    lg: scale(24),
    xl: scale(32),
    xxl: scale(48),
    xxxl: scale(64),
  },

  // Border Radius
  borderRadius: {
    none: 0,
    sm: scale(4),
    md: scale(8),
    lg: scale(12),
    xl: scale(16),
    full: 1000, // For fully rounded elements
  },

  // Border Width
  borderWidth: {
    none: 0,
    thin: 1,
    medium: 2,
    thick: 4,
  },

  // Shadows (for future use)
  shadows: {
    sm: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    md: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 4,
    },
    lg: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 8,
    },
  },

  // Z-Index
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
} as const;

// Type definitions for better TypeScript support
export type ColorToken = typeof tokens.colors;
export type TypographyToken = typeof tokens.typography;
export type SpacingToken = typeof tokens.spacing;
export type BorderRadiusToken = typeof tokens.borderRadius;
export type BorderWidthToken = typeof tokens.borderWidth;
export type ShadowToken = typeof tokens.shadows;
export type ZIndexToken = typeof tokens.zIndex;
